import i18n from '../../../../i18n';
import { formatHorizonNumber, formatServiceDate, metricAvailable } from './horizonFormat';
import { PRESSURE_PCT, signalDates, type HorizonDay } from './horizonCalendar';
import type {
  HorizonBookingDay,
  HorizonCourseCell,
  HorizonSignal,
} from './horizonTypes';

/** Days shown by default. The full booking window stays one click away. */
export const NEAR_DAYS = 7;

/** Distinct service dates in the signal set, never more than the window. */
export function attentionDayCount(
  signals: HorizonSignal[],
  cap: number,
): number {
  return Math.min(signalDates(signals, null).size, Math.max(0, cap));
}

export type HorizonTileTone = 'risk' | 'win' | 'watch';

export type HorizonTile = {
  key: string;
  tone: HorizonTileTone;
  icon: string;
  label: string;
  value: string;
  detail: string;
  action?: string;
};

export type HorizonHeadline = {
  headline: string;
  subline: string;
};

/** Counted totals over a stretch of days. Never mixes closed days into capacity. */
export type NearWindowTotals = {
  days: HorizonDay[];
  openDays: HorizonDay[];
  covers: number;
  capacity: number;
  occupancyPct: number | null;
  fullest: HorizonDay | null;
  emptiest: HorizonDay | null;
  atCapacity: HorizonDay[];
  closedDays: HorizonDay[];
};

function num(value: number): string {
  return formatHorizonNumber(value);
}

function pct(value: number): string {
  return `${Math.round(value)}%`;
}

/** Short weekday + date, e.g. "Sat 22 Aug". */
function dayLabel(day: HorizonDay): string {
  return formatServiceDate(day.serviceDate);
}

export function nearWindowTotals(
  allDays: HorizonDay[],
  span = NEAR_DAYS,
): NearWindowTotals {
  const days = allDays.slice(0, Math.max(1, span));
  const openDays = days.filter(d => d.state === 'open');

  let covers = 0;
  let capacity = 0;
  for (const day of openDays) {
    covers += day.onBooks ?? 0;
    capacity += day.capacity ?? 0;
  }

  const measurable = openDays.filter(d => d.occupancyPct != null);
  const sorted = [...measurable].sort(
    (a, b) => (b.occupancyPct as number) - (a.occupancyPct as number),
  );

  return {
    days,
    openDays,
    covers,
    capacity,
    occupancyPct: capacity > 0 ? (covers / capacity) * 100 : null,
    fullest: sorted[0] ?? null,
    emptiest: sorted.length > 1 ? sorted[sorted.length - 1]! : null,
    atCapacity: measurable.filter(
      d => (d.occupancyPct as number) >= PRESSURE_PCT,
    ),
    closedDays: days.filter(d => d.state === 'closed'),
  };
}

/**
 * The one sentence a manager should read first: how full the near window is,
 * then where the pressure and the slack sit.
 */
export function buildHeadline(
  totals: NearWindowTotals,
  attentionCount: number,
  span = NEAR_DAYS,
): HorizonHeadline {
  const { covers, capacity, occupancyPct, fullest, emptiest } = totals;

  if (totals.openDays.length === 0) {
    return {
      headline: i18n.t('horizon:highlights.noBookable', { span }),
      subline:
        totals.closedDays.length > 0
          ? i18n.t('horizon:highlights.allClosed')
          : i18n.t('horizon:highlights.pastWindow'),
    };
  }

  const headline =
    occupancyPct != null
      ? i18n.t('horizon:highlights.guestsBookedOcc', {
          covers: num(covers),
          span,
          pct: pct(occupancyPct),
        })
      : i18n.t('horizon:highlights.guestsBooked', { covers: num(covers), span });

  const parts: string[] = [];
  if (capacity > 0) {
    parts.push(i18n.t('horizon:highlights.coversAvailable', { capacity: num(capacity) }));
  }
  if (fullest && fullest.occupancyPct != null) {
    parts.push(
      i18n.t('horizon:highlights.tightest', {
        day: dayLabel(fullest),
        pct: pct(fullest.occupancyPct),
      }),
    );
  }
  if (emptiest && emptiest.occupancyPct != null && emptiest !== fullest) {
    const room = Math.max(0, (emptiest.capacity ?? 0) - (emptiest.onBooks ?? 0));
    parts.push(
      room > 0
        ? i18n.t('horizon:highlights.mostRoomOpen', {
            day: dayLabel(emptiest),
            room: num(room),
          })
        : i18n.t('horizon:highlights.mostRoomPct', {
            day: dayLabel(emptiest),
            pct: pct(emptiest.occupancyPct),
          }),
    );
  }
  if (attentionCount > 0) {
    parts.push(
      attentionCount === 1
        ? i18n.t('horizon:highlights.dateNeeds', { count: attentionCount })
        : i18n.t('horizon:highlights.datesNeed', { count: attentionCount }),
    );
  }

  return { headline, subline: parts.length ? `${parts.join('. ')}.` : '' };
}

/**
 * Highlight tiles for the hero. Booking-derived tiles only appear once an
 * outlet is focused, because reservation detail is fetched one outlet at a time.
 */
export function buildHighlightTiles({
  totals,
  bookingDays,
  attentionCount,
  attentionSummary,
  span = NEAR_DAYS,
}: {
  totals: NearWindowTotals;
  bookingDays?: HorizonBookingDay[] | null;
  attentionCount: number;
  attentionSummary?: string | null;
  span?: number;
}): HorizonTile[] {
  const tiles: HorizonTile[] = [];
  const { covers, capacity, occupancyPct, fullest, emptiest, atCapacity } =
    totals;

  if (attentionCount > 0) {
    tiles.push({
      key: 'attention',
      tone: 'risk',
      icon: 'flag',
      label: i18n.t('horizon:highlights.datesDecision'),
      value: i18n.t('horizon:highlights.ofDays', {
        count: attentionCount,
        total: totals.days.length,
      }),
      detail:
        attentionSummary ||
        i18n.t('horizon:highlights.behindPace'),
      action: i18n.t('horizon:highlights.openNeeds'),
    });
  }

  if (occupancyPct != null) {
    tiles.push({
      key: 'sold',
      tone: occupancyPct >= 60 ? 'win' : 'watch',
      icon: 'users',
      label: i18n.t('horizon:highlights.guestsBookedNext', { span }),
      value: num(covers),
      detail: i18n.t('horizon:highlights.countedOnBooks', {
        pct: pct(occupancyPct),
        capacity: num(capacity),
      }),
      action:
        occupancyPct < 60
          ? i18n.t('horizon:highlights.stillOpen', {
              count: num(Math.max(0, capacity - covers)),
            })
          : undefined,
    });
  }

  if (fullest?.occupancyPct != null) {
    const left = Math.max(0, (fullest.capacity ?? 0) - (fullest.onBooks ?? 0));
    tiles.push({
      key: 'fullest',
      tone: 'win',
      icon: 'trendUp',
      label: i18n.t('horizon:highlights.fullestDay'),
      value: dayLabel(fullest),
      detail: i18n.t('horizon:highlights.fullestDetail', {
        pct: pct(fullest.occupancyPct),
        booked: num(fullest.onBooks ?? 0),
        capacity: num(fullest.capacity ?? 0),
        left: num(left),
      }),
      action:
        left === 0
          ? i18n.t('horizon:highlights.nothingLeft')
          : undefined,
    });
  }

  if (emptiest?.occupancyPct != null && emptiest !== fullest) {
    const room = Math.max(0, (emptiest.capacity ?? 0) - (emptiest.onBooks ?? 0));
    tiles.push({
      key: 'emptiest',
      tone: 'watch',
      icon: 'arrowDown',
      label: i18n.t('horizon:highlights.mostRoom'),
      value: dayLabel(emptiest),
      detail: i18n.t('horizon:highlights.emptiestDetail', {
        pct: pct(emptiest.occupancyPct),
        booked: num(emptiest.onBooks ?? 0),
        capacity: num(emptiest.capacity ?? 0),
        room: num(room),
      }),
      action: i18n.t('horizon:highlights.extraDemand'),
    });
  }

  if (atCapacity.length > 0) {
    tiles.push({
      key: 'pressure',
      tone: 'win',
      icon: 'bolt',
      label: i18n.t('horizon:highlights.daysFull'),
      value: i18n.t('horizon:highlights.ofDays', {
        count: atCapacity.length,
        total: totals.openDays.length,
      }),
      detail: i18n.t('horizon:highlights.atCapacity', {
        dates: atCapacity.map(dayLabel).join(', '),
        verb: atCapacity.length === 1 ? i18n.t('horizon:highlights.is') : i18n.t('horizon:highlights.are'),
        pct: PRESSURE_PCT,
      }),
      action: i18n.t('horizon:highlights.waitlist'),
    });
  }

  const booked = (bookingDays ?? []).filter(
    d => !d.unavailableReason && d.summary.parties > 0,
  );
  if (booked.length > 0) {
    const parties = booked.reduce((sum, d) => sum + d.summary.parties, 0);
    const vips = booked.reduce((sum, d) => sum + d.summary.vips, 0);
    const largest = booked.reduce(
      (max, d) => Math.max(max, d.summary.largestParty),
      0,
    );
    tiles.push({
      key: 'parties',
      tone: 'watch',
      icon: 'table',
      label: i18n.t('horizon:highlights.bookingsToSeat'),
      value: num(parties),
      detail:
        i18n.t('horizon:highlights.partiesDetail', {
          parties: num(parties),
          days: booked.length,
          dayWord: booked.length === 1 ? i18n.t('horizon:dayWord') : i18n.t('horizon:daysWord'),
          largest: num(largest),
        }) +
        (vips > 0 ? i18n.t('horizon:highlights.flaggedVip', { count: num(vips) }) : ''),
      action: i18n.t('horizon:highlights.listsTags'),
    });

    const topTag = topGuestTag(booked);
    if (topTag) {
      tiles.push({
        key: 'tag',
        tone: 'watch',
        icon: 'star',
        label: i18n.t('horizon:highlights.commonTag'),
        value: topTag.label,
        detail: i18n.t('horizon:highlights.tagDetail', {
          count: num(topTag.count),
          parties: num(parties),
        }),
      });
    }
  }

  return tiles.slice(0, 6);
}

/** One clickable row in the hero rail: an outlet worth opening, and why. */
export type HorizonOutletSignal = {
  outletId: string;
  outletName: string;
  tone: HorizonTileTone;
  icon: string;
  value: string;
  detail: string;
};

export type HorizonOutletRail = {
  /** Group heading for the first band, which depends on what the fleet shows. */
  firstLabel: string;
  first: HorizonOutletSignal[];
  firstTotal: number;
  fullest: HorizonOutletSignal[];
};

type OutletRollup = {
  outletId: string;
  outletName: string;
  covers: number;
  capacity: number;
  occupancyPct: number | null;
  openCovers: number;
};

function rollUpOutlets(
  cells: HorizonCourseCell[],
  nearDates: Set<string>,
): Map<string, OutletRollup> {
  const byOutlet = new Map<string, OutletRollup>();
  for (const cell of cells) {
    if (!nearDates.has(cell.serviceDate)) continue;
    if (cell.kind !== 'project' && cell.kind !== 'pace_only') continue;
    let entry = byOutlet.get(cell.outletId);
    if (!entry) {
      entry = {
        outletId: cell.outletId,
        outletName: cell.outletName,
        covers: 0,
        capacity: 0,
        occupancyPct: null,
        openCovers: 0,
      };
      byOutlet.set(cell.outletId, entry);
    }
    if (metricAvailable(cell.onBooks)) entry.covers += cell.onBooks.value as number;
    if (metricAvailable(cell.coverCapacity)) {
      entry.capacity += cell.coverCapacity.value as number;
    }
  }
  for (const entry of byOutlet.values()) {
    entry.occupancyPct =
      entry.capacity > 0 ? (entry.covers / entry.capacity) * 100 : null;
    entry.openCovers = Math.max(0, entry.capacity - entry.covers);
  }
  return byOutlet;
}

/**
 * Which outlets a fleet manager should open first. Signals win when present;
 * otherwise the rail points at the largest unsold capacity, which is the honest
 * answer to "where is my attention worth most" when nothing is actually wrong.
 */
export function buildOutletRail({
  cells,
  signals,
  nearDates,
  span = NEAR_DAYS,
  limit = 3,
}: {
  cells: HorizonCourseCell[];
  signals: HorizonSignal[];
  /** The dates the week agenda is showing, so the rail and the hero agree. */
  nearDates: Set<string>;
  span?: number;
  limit?: number;
}): HorizonOutletRail {
  const rollups = rollUpOutlets(cells, nearDates);

  const byOutletSignals = new Map<string, HorizonSignal[]>();
  for (const signal of signals) {
    const outletId = signal.outletId;
    if (!outletId) continue;
    const date =
      signal.link?.serviceDate ||
      (signal.evidence.kind === 'horizonDate' ? signal.evidence.serviceDate : null);
    if (date && !nearDates.has(date)) continue;
    const list = byOutletSignals.get(outletId);
    if (list) list.push(signal);
    else byOutletSignals.set(outletId, [signal]);
  }

  const flagged: HorizonOutletSignal[] = [...byOutletSignals.entries()]
    // More flagged dates first, compared as numbers: "10 dates" outranks "9 dates".
    .sort(([, a], [, b]) => b.length - a.length)
    .map(([outletId, list]) => {
      const roll = rollups.get(outletId);
      const soonest = [...list].sort((a, b) => {
        const da = a.link?.serviceDate ?? '';
        const db = b.link?.serviceDate ?? '';
        return da.localeCompare(db);
      })[0]!;
      const when = soonest.link?.serviceDate;
      return {
        outletId,
        outletName: roll?.outletName || outletId,
        tone: 'risk' as const,
        icon: 'flag',
        value: `${list.length} ${list.length === 1 ? i18n.t('horizon:highlights.date') : i18n.t('horizon:highlights.dates')}`,
        detail: when
          ? i18n.t('horizon:highlights.soonest', {
              date: formatServiceDate(when),
              insight: soonest.insight,
            })
          : soonest.insight,
      };
    });

  const measurable = [...rollups.values()].filter(r => r.occupancyPct != null);

  const roomiest: HorizonOutletSignal[] = [...measurable]
    .sort((a, b) => b.openCovers - a.openCovers)
    .slice(0, limit)
    .map(roll => ({
      outletId: roll.outletId,
      outletName: roll.outletName,
      tone: 'watch' as const,
      icon: 'arrowDown',
      value: i18n.t('horizon:highlights.openCovers', { count: num(roll.openCovers) }),
      detail: i18n.t('horizon:highlights.fullOver', {
        pct: pct(roll.occupancyPct as number),
        span,
        covers: num(roll.covers),
        capacity: num(roll.capacity),
      }),
    }));

  const fullest: HorizonOutletSignal[] = [...measurable]
    .sort((a, b) => (b.occupancyPct as number) - (a.occupancyPct as number))
    .slice(0, limit)
    .map(roll => ({
      outletId: roll.outletId,
      outletName: roll.outletName,
      tone: 'win' as const,
      icon: 'trendUp',
      value: pct(roll.occupancyPct as number),
      detail: i18n.t('horizon:highlights.bookedOver', {
        covers: num(roll.covers),
        capacity: num(roll.capacity),
        span,
      }),
    }));

  const useFlagged = flagged.length > 0;
  return {
    firstLabel: useFlagged
      ? i18n.t('horizon:needsAttention')
      : i18n.t('horizon:highlights.mostRoom'),
    first: useFlagged ? flagged.slice(0, limit) : roomiest,
    firstTotal: useFlagged ? flagged.length : roomiest.length,
    fullest,
  };
}

function topGuestTag(
  days: HorizonBookingDay[],
): { label: string; count: number } | null {
  const counts = new Map<string, { label: string; count: number }>();
  for (const day of days) {
    for (const tag of day.summary.tagCounts) {
      const name = (tag.name || '').trim();
      if (!name) continue;
      const key = name.toLowerCase();
      const entry = counts.get(key);
      if (entry) entry.count += tag.count;
      else counts.set(key, { label: name, count: tag.count });
    }
  }
  const best = [...counts.values()].sort((a, b) => b.count - a.count)[0];
  return best && best.count > 0 ? best : null;
}
